import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';

import { Token } from '@proto/store/api/auth/resources';
import { PreferenceKey } from '@helper/preference-key';
import { AuthStateInterface, IDToken } from '@service/auth-state.interface';
import { RouteHelper } from '@helper/route-paths';
import { RouterHistoryService } from '@service/router-history.service';
import { CsDeferService } from '@service/cs-defer.service';
import { CryptHelper } from '@helper/crypt-helper';
import { LegacySafeStorageService } from '@service/legacy/legacy-safe-storage.service';

import decode from 'jwt-decode';
import { from, Subject } from 'rxjs';
import { getAuth, signOut } from 'firebase/auth';

@Injectable({
  providedIn: 'root',
})
export class AuthStateService implements AuthStateInterface {
  readonly logoutEmitter = new Subject<boolean>();

  private readonly isServer = isPlatformServer(this.platformId);

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private routerHistoryService: RouterHistoryService,
    private safeStorageService: LegacySafeStorageService,
    private csDeferService: CsDeferService
  ) {}

  isLogin(): boolean {
    if (this.isServer) {
      return false;
    }
    const token = this.getToken();
    // 有 accessToken 跟 refreshToken 就算登入.
    // 但打 api 時, refreshTokenIfNeeded 會檢查有沒有過期.
    // 有過期就會 refreshToken, 然後維持登入狀態.
    // 但如果真的都過期, 就會 logout, 就會清空 token.
    return !!(token.accessToken && token.refreshToken);
  }

  getUidByToken(token: Token): string | undefined {
    if (this.isServer || !token.idToken) {
      return undefined;
    }
    const idToken: IDToken | undefined = decode(token.idToken);
    return idToken?.uid;
  }

  getIDToken(): IDToken | undefined {
    if (this.isServer) {
      return undefined;
    }
    const idToken = (this.getToken() ?? Token.fromPartial({})).idToken;
    if (!idToken) {
      return undefined;
    }
    return decode(idToken);
  }

  setToken(token: Token): void {
    if (this.isServer) {
      return;
    }
    try {
      localStorage?.setItem(PreferenceKey.ACCESS_TOKEN, JSON.stringify(Token.toJSON(token)));
    } catch {
      // ignore
    }
  }

  getToken(): Token {
    if (this.isServer) {
      return Token.fromPartial({});
    }
    try {
      const jsonStr = localStorage?.getItem(PreferenceKey.ACCESS_TOKEN);
      const token = jsonStr ? Token.fromJSON(JSON.parse(jsonStr)) : Token.fromPartial({});
      if (token.accessToken) {
        try {
          // pass original token
          decode(token.accessToken);
        } catch {
          // token is encrypted
          token.accessToken = atob(
            CryptHelper.decryptWithKey(token.accessToken, localStorage?.getItem(PreferenceKey.INSTANCE_ID) || '')
          );
        }
      }
      return token;
    } catch {
      return Token.fromPartial({});
    }
  }

  logout(toLogin?: boolean): void {
    if (this.isServer) {
      return;
    }
    // 全部需要清除的資料都要寫在這.
    this.safeStorageService.clearLegacyAll();
    this.csDeferService.clear();
    try {
      localStorage?.removeItem(PreferenceKey.ACCESS_TOKEN);
      localStorage?.removeItem(PreferenceKey.CART);
      localStorage?.removeItem(PreferenceKey.FAVORITE);
      localStorage?.removeItem(PreferenceKey.PHONE);
      localStorage?.removeItem(PreferenceKey.ADDRESS);
      localStorage?.removeItem(PreferenceKey.CAN_READ_PROFILE);
      localStorage?.removeItem(PreferenceKey.LOGIN_TYPE);
      localStorage?.removeItem(PreferenceKey.LOGIN_PHONE);
      localStorage?.removeItem(PreferenceKey.CS_LAST_READ_ID);
      localStorage?.removeItem(PreferenceKey.CS_UNREAD_INFO);
      localStorage?.removeItem(PreferenceKey.PROFILE_PIC_URL);
      localStorage?.removeItem(PreferenceKey.LAST_SEND_CODE_TIME);
    } catch {
      // ignore
    }

    from(signOut(getAuth())).subscribe();
    this.logoutEmitter.next(true);
    this.routerHistoryService.forceGoHome();
    if (toLogin) {
      this.routerHistoryService.goRoute(RouteHelper.login());
    }
  }
}
